import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types/supabase';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('CRITICAL ERROR: Supabase environment variables are missing!');
}

const fetchConcurrency = 2;
let activeFetches = 0;
const fetchWaiters: Array<() => void> = [];

const limitedFetch: typeof fetch = async (...args) => {
  if (activeFetches >= fetchConcurrency) {
    await new Promise<void>(resolve => fetchWaiters.push(resolve));
  }
  activeFetches += 1;
  try {
    return await fetch(...args);
  } finally {
    activeFetches -= 1;
    const next = fetchWaiters.shift();
    if (next) next();
  }
};

declare global {
  interface Window {
    __KWACHALITE_SUPABASE_INSTANCE__?: ReturnType<typeof createClient<Database>>;
  }
}

export const supabase = (() => {
  if (typeof window !== 'undefined' && window.__KWACHALITE_SUPABASE_INSTANCE__) {
    return window.__KWACHALITE_SUPABASE_INSTANCE__;
  }

  const instance = createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      storage: typeof window !== 'undefined' ? window.localStorage : undefined,
      storageKey: 'kwachalite-auth',
      flowType: 'pkce',
    },
    realtime: { params: { eventsPerSecond: 2 } },
    db: { schema: 'public' },
    global: {
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      fetch: limitedFetch,
    },
  });

  if (typeof window !== 'undefined') {
    window.__KWACHALITE_SUPABASE_INSTANCE__ = instance;
  }

  return instance;
})();

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  const originalWarn = console.warn;
  console.warn = (...args) => {
    if (typeof args[0] === 'string' && args[0].includes('Multiple GoTrueClient instances detected')) return;
    originalWarn.apply(console, args);
  };
}
